import { useCallback, useRef } from "react";
import type { FormikErrors, FormikTouched } from "formik";
import type { LeaveSaveOutcome, SaveBeforeLeaveResult } from "../core/saveBeforeLeaveOutcome";
import {
    hasFormikValidationErrors,
    toSaveBeforeLeaveResult,
    touchAllFormikErrors,
} from "./formikLeaveSaveState";

export type UseFormikSaveBeforeLeaveOptions<T> = {
    submitForm: () => Promise<void | FormikErrors<T>>;
    validateForm: () => Promise<FormikErrors<T>>;
    setTouched: (touched: FormikTouched<T>, shouldValidate?: boolean) => void;
    leaveAfterSaveRef: React.MutableRefObject<boolean>;
    saveOutcomeRef: React.MutableRefObject<LeaveSaveOutcome>;
};

/** Returns a save handler for useUnsavedChangesGuard onSave (Save and continue). */
export function useFormikSaveBeforeLeave<T>({
    submitForm,
    validateForm,
    setTouched,
    leaveAfterSaveRef,
    saveOutcomeRef,
}: UseFormikSaveBeforeLeaveOptions<T>): () => Promise<SaveBeforeLeaveResult> {
    const submitFormRef = useRef(submitForm);
    const validateFormRef = useRef(validateForm);
    const setTouchedRef = useRef(setTouched);
    submitFormRef.current = submitForm;
    validateFormRef.current = validateForm;
    setTouchedRef.current = setTouched;

    return useCallback(async () => {
        const errors = await validateFormRef.current();
        if (hasFormikValidationErrors(errors)) {
            touchAllFormikErrors(errors, setTouchedRef.current);
            return "validation_failed";
        }

        leaveAfterSaveRef.current = true;
        saveOutcomeRef.current = null;

        try {
            await submitFormRef.current();
        } finally {
            leaveAfterSaveRef.current = false;
        }

        return toSaveBeforeLeaveResult(saveOutcomeRef.current);
    }, [leaveAfterSaveRef, saveOutcomeRef]);
}
